import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Eyebrow, Mono } from '../components/section';
import { brand } from '../thema';

type Value = { name: string; title: string; description: string };

const titleSx = {
    mt: 2.5,
    fontSize: { xs: '1.375rem', md: '2.25rem' },
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: '0.03em',
    whiteSpace: 'pre-line',
};

const bodySx = {
    fontSize: { xs: '0.9375rem', md: '1rem' },
    lineHeight: 2,
    color: brand.inkSub,
};

// 会社情報ページの Vision / Mission / Philosophy / Values / Key Message
const CompanyPhilosophy = () => {
    const { t } = useTranslation();
    const list = <T,>(key: string) => t(key, { returnObjects: true }) as unknown as T[];

    const Paragraphs = ({ keyName, sx }: { keyName: string; sx?: object }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 760, ...sx }}>
            {list<string>(keyName).map((p) => (
                <Typography key={p} sx={bodySx}>{p}</Typography>
            ))}
        </Box>
    );

    return (
        <Box sx={{ mt: { xs: 4, md: 8 }, mr: { xs: 3, md: 10 }, color: brand.ink }}>
            {/* Vision */}
            <Box component="section">
                <Eyebrow index="01" label={t('corporate.vision.eyebrow')} />
                <Typography component="h2" sx={titleSx}>{t('corporate.vision.title')}</Typography>
                <Paragraphs keyName="corporate.vision.paragraphs" sx={{ mt: 4 }} />
            </Box>

            {/* Mission */}
            <Box component="section" sx={{ mt: { xs: 10, md: 14 }, pt: { xs: 6, md: 8 }, borderTop: `1px solid ${brand.line}` }}>
                <Eyebrow index="02" label={t('corporate.mission.eyebrow')} />
                <Typography component="h2" sx={titleSx}>{t('corporate.mission.title')}</Typography>
                <Paragraphs keyName="corporate.mission.paragraphs" sx={{ mt: 4 }} />
                <Box component="ul" sx={{ listStyle: 'none', p: 0, my: 4, pl: { xs: 2.5, md: 3 }, borderLeft: `2px solid ${brand.accent}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {list<string>('corporate.mission.questions').map((q) => (
                        <Typography component="li" key={q} sx={{ fontSize: { xs: '1.0625rem', md: '1.25rem' }, fontWeight: 500, lineHeight: 1.8 }}>{q}</Typography>
                    ))}
                </Box>
                <Paragraphs keyName="corporate.mission.closing" />
            </Box>

            {/* Our Development Philosophy */}
            <Box
                component="section"
                sx={{
                    mt: { xs: 10, md: 14 },
                    p: { xs: 3, md: 8 },
                    backgroundColor: brand.dark,
                    color: '#ffffff',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px)',
                    backgroundSize: '56px 56px',
                }}
            >
                <Eyebrow index="03" label={t('corporate.philosophy.eyebrow')} tone="dark" />
                <Typography component="h2" sx={{ ...titleSx, fontWeight: 500, fontSize: { xs: '1.25rem', md: '2.25rem' } }}>
                    {t('corporate.philosophy.title')}
                </Typography>
                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 760 }}>
                    {list<string>('corporate.philosophy.paragraphs').map((p) => (
                        <Typography key={p} sx={{ ...bodySx, color: 'rgba(255, 255, 255, 0.78)' }}>{p}</Typography>
                    ))}
                </Box>
                <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 3, rowGap: 1 }}>
                    <Mono size="1.5rem" color={brand.accent}>{t('corporate.philosophy.signature')}</Mono>
                    <Typography sx={{ fontSize: '0.9375rem', color: 'rgba(255, 255, 255, 0.78)' }}>{t('corporate.philosophy.signatureNote')}</Typography>
                </Box>
            </Box>

            {/* Values */}
            <Box component="section" sx={{ mt: { xs: 10, md: 14 } }}>
                <Eyebrow index="04" label={t('corporate.values.eyebrow')} />
                <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 5, md: 5 } }}>
                    {list<Value>('corporate.values.items').map((value, i) => (
                        <Box key={value.name} sx={{ borderTop: `1px solid ${brand.ink}`, pt: 3 }}>
                            <Mono color={brand.accentText}>{String(i + 1).padStart(2, '0')}. {value.name}</Mono>
                            <Typography component="h3" sx={{ mt: 1.5, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.6 }}>{value.title}</Typography>
                            <Typography sx={{ mt: 1.5, fontSize: '0.9375rem', lineHeight: 1.9, color: brand.inkSub }}>{value.description}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Key Message */}
            <Box component="section" sx={{ mt: { xs: 10, md: 14 }, pt: { xs: 6, md: 8 }, borderTop: `1px solid ${brand.line}` }}>
                <Eyebrow index="05" label={t('corporate.keyMessage.eyebrow')} />
                <Typography component="h2" sx={{ ...titleSx, fontSize: { xs: '1.5rem', md: '2.75rem' } }}>{t('corporate.keyMessage.title')}</Typography>
                <Paragraphs keyName="corporate.keyMessage.paragraphs" sx={{ mt: 4 }} />
                <Typography sx={{ mt: 2, maxWidth: 760, fontSize: { xs: '1.0625rem', md: '1.25rem' }, fontWeight: 700, lineHeight: 1.8 }}>
                    {t('corporate.keyMessage.emphasis')}
                </Typography>
                <Typography sx={{ ...bodySx, mt: 2, maxWidth: 760 }}>{t('corporate.keyMessage.closing')}</Typography>
            </Box>
        </Box>
    );
};

export default CompanyPhilosophy;
